import { computed, type Ref } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { EventStatus, type EventResponse, type TournamentResponse } from "@xs2event/js-lib/api";
import { useLocalePortalEventsCache } from "@/composables/events";
import { PORTAL_TOURNAMENTS_CACHE_STALE_TIME, usePortalTournamentsCache } from "@/composables/tournaments";
import { eventsWithTournament } from "@/constants/routes";
import { toEvent } from "@/components/event/index.js";
import brandconfig, { isB2C } from "@/brandconfig";

export type SuggestedSearch = { name: string; type: string; to: RouteLocationRaw };

const TOURNAMENT_COUNT = 2;
const EVENT_COUNT = 6;

const toSuggestedTournament = (tournament: TournamentResponse): SuggestedSearch => ({
  name: `${tournament.officialName} (${tournament.season})`,
  type: "Tournament",
  to: eventsWithTournament(tournament.slug!.toLowerCase()),
});

const toSuggestedEvent = (event: EventResponse): SuggestedSearch => ({
  name: event.eventName,
  type: "Event",
  to: toEvent({ slug: event.slug, event_id: event.eventId }),
});

/**
 * What to offer in the search panel before anything is typed: the next events coming up and the
 * tournaments they belong to, read from the portal events and tournaments caches.
 *
 * Only events that are still to start and have tickets in stock are offered. A tournament counts as
 * upcoming by its next such event rather than its start date -- a running season started months ago
 * but may well play this weekend -- so every tournament offered has events to book as well.
 *
 * A branded shop only offers its own sports (no football on GPFans), by its `SPORT_TYPES`. The
 * B2B portal sells every sport, so it offers them all.
 *
 * `enabled` keeps both caches off until the panel is actually opened; the header mounts on every page.
 */
export const useSuggestedSearches = (enabled: Ref<boolean>) => {
  const eventsQuery = useLocalePortalEventsCache({ enabled });
  const tournamentsQuery = usePortalTournamentsCache({ enabled, staleTime: PORTAL_TOURNAMENTS_CACHE_STALE_TIME });

  const brandSportTypes: string[] | null = isB2C ? (brandconfig.SPORT_TYPES ?? null) : null;

  // Every event of the brand's sports still to start with tickets in stock, soonest first.
  const upcomingEvents = computed(() =>
    (eventsQuery.data.value?.events ?? [])
      .filter(
        (event) =>
          event.eventStatus === EventStatus.Notstarted &&
          (event.numberOfTickets ?? 0) > 0 &&
          (!brandSportTypes || brandSportTypes.includes(event.sportType))
      )
      .sort((a, b) => a.dateStart.localeCompare(b.dateStart))
  );

  // The tournaments of the soonest events above, in the order their events come up. Without a slug a
  // tournament has no page to link to, and one the backend counts no events for has nothing to show.
  const upcomingTournaments = computed(() => {
    const byId = new Map(
      (tournamentsQuery.data.value?.tournaments ?? [])
        .filter((tournament) => !!tournament.slug && (tournament.numberEvents ?? 0) > 0)
        .map((tournament) => [tournament.tournamentId, tournament])
    );

    const tournaments: TournamentResponse[] = [];
    for (const event of upcomingEvents.value) {
      const tournament = byId.get(event.tournamentId);
      if (tournament && !tournaments.includes(tournament)) tournaments.push(tournament);
      if (tournaments.length === TOURNAMENT_COUNT) break;
    }
    return tournaments;
  });

  const suggestions = computed<SuggestedSearch[]>(() => [
    ...upcomingTournaments.value.map(toSuggestedTournament),
    ...upcomingEvents.value.slice(0, EVENT_COUNT).map(toSuggestedEvent),
  ]);

  // Checked on `isFetched` rather than `isLoading`: a disabled query is not loading either.
  const loading = computed(() => enabled.value && (!eventsQuery.isFetched.value || !tournamentsQuery.isFetched.value));

  return { suggestions, loading };
};
